<template>
  <sc-render
    :fields="fields"
    :emptyMessage="$t('NO_DATA_SOURCE_EXIST')"
  >
    <div
      class="image-landscape"
      :class="[textPosition, { 'theme-dark' : isDarkTheme }]"
    >
      <div
        class="image-container"
        :class="{ clickable: hasLinkHref }"
        @click="handleImageClick"
      >
        <media-display
          v-if="imageSrc"
          class="media-display"
          :class="{ 'media-display--edit-mode': isEditMode }"
          :sc-image-field="image"
          :image-desktop-source="imageSrc"
          :video-source="videoUrl"
          :video-loop="loopVideo"
          :video-hidden-text="hiddenVideoTitle"
        />
        <div
          v-if="isEditMode || showCardDescription"
          class="card-description"
          :class="{ 'theme-dark' : isDarkTheme }"
        >
          <sc-text
            v-if="isEditMode || hasTitleValue"
            class="title"
            tag="h2"
            :field="title"
          />
          <sc-text
            v-if="isEditMode || hasDescriptionValue"
            class="description"
            tag="div"
            :field="description"
          />
        </div>
      </div>
      <div
        v-if="isEditMode || hasLinkHref"
        class="image-landscape__cta"
      >
        <sc-link
          v-if="isEditMode"
          class="sc-link"
          :field="link"
        />
        <sh-button
          v-else
          ref="ctaButton"
          :[btnLinkKey]="link.value.href"
          :target="link.value.target"
          :naked="true"
        >
          {{ link.value.text }}
        </sh-button>
      </div>
    </div>
  </sc-render>
</template>

<script>

import { Text, Link } from '@sitecore-jss/sitecore-jss-vue'
import { createNamespacedHelpers } from 'vuex'
import { getButtonType } from '~/helpers/get-button-type'
import get from '~/helpers/get'
import ThemeMixin from '~/mixins/theme'
import mapFields from '~/helpers/map-fields'
import MediaDisplay from '~/components/sitecore/media-display'
import ScRender from '../sc-render'

const { mapGetters: mapJssGetters } = createNamespacedHelpers('jss')

export default {
  name: 'LandscapeImage',

  props: {
    fields: {
      type: Object,
      default: () => ({})
    }
  },

  components: {
    ScRender,
    ScText: Text,
    ScLink: Link,
    MediaDisplay
  },

  mixins: [ThemeMixin],

  computed: {
    ...mapJssGetters(['isEditMode']),
    ...mapFields({
      videoUrl: 'mainVideo.value',
      hiddenVideoTitle: 'hiddenVideoTitle.value',
      loopVideo: 'loop.value',
      image: 'image',
      imageValue: 'image.value'
    }),

    textPosition () {
      return get(this.fields, 'textPosition.value', '').toLowerCase()
    },

    link () {
      return get(this.fields, 'link', false)
    },

    hasLinkHref () {
      return !!(this.link && this.link.value.href)
    },

    imageSrc () {
      if (!this.imageValue) return ''

      return this.imageValue.cloudinary_4x3_fill_crop || this.imageValue.src
    },

    title () {
      return get(this.fields, 'title', false)
    },

    hasTitleValue () {
      return this.title && this.title.value
    },

    description () {
      return get(this.fields, 'description', false)
    },

    hasDescriptionValue () {
      return this.description && this.description.value
    },

    showCardDescription () {
      return this.hasTitleValue || this.hasDescriptionValue || this.hasLinkHref
    },

    btnLinkKey () {
      if (!this.hasLinkHref) return ''

      return getButtonType(this.link.value.href)
    }
  },

  methods: {
    handleImageClick () {
      if (!this.$refs.ctaButton) return

      this.$refs.ctaButton.$el.click()
    }
  }
}
</script>

<style lang="scss" scoped>

.image-landscape {
  margin: 0 auto $space-090;

  .media-display {
    z-index: -1;

    &--edit-mode {
      z-index: 0;
    }
  }

  @include tablet {
    max-width: 55%;
    margin: 0 auto $space-110;
  }

  .image-container {
    &.clickable {
      cursor: pointer;
    }
  }

  &__cta {
    padding: 0 $space-050;

    @include tablet {
      @include reset
    }
  }

  &.center {
    text-align: center;
  }

  .sc-link {
    @include font('040');

    font-weight: 700;
    text-decoration: none;
  }

  .card-description {
    margin-top: $space-060;
    padding: 0 $space-050;

    @include tablet {
      margin-top: $space-040;
      padding: unset;
    }
  }

  .title {
    @include font('060');

    margin-bottom: $space-020;
  }

  .description {
    @include font('040');

    margin-bottom: $space-020;
  }
}

.theme-dark {
  color: $color-white;

  ::v-deep a {
    color: $color-white;
  }
}
</style>
